// @ts-check
import { Component } from '@theme/component';
import { fetchConfig, lockScroll, unlockScroll } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Lets a shopper switch a cart line to another variant of the same product (size, colour, …)
 * through a confirm dialog — markup and data in snippets/cart-line-variant-picker.liquid.
 *
 * There is no cart API call that changes a line's merchandise in place, so Confirm does two
 * requests: add the chosen variant (`/cart/add.js` — merges into an existing line for it, if the
 * shopper already has one), then zero this line out by its **key** through `/cart/update`. Add
 * goes first so a failure (sold out, stock limit) never costs the shopper the line they already
 * have.
 *
 * ⚠️ Removal is addressed by line **key**, never by line number, and that is the fix for a real
 * bug: the first version removed by a 1-based line number captured at page render. Shopify puts a
 * newly added line at the *start* of `cart.items`, so the add shifts every existing line down by
 * one — the stale number then pointed at the wrong line, and the swap appeared to "reload and
 * keep the old size". A key is assigned by Shopify from the variant + properties and doesn't move.
 *
 * The UI refresh is not done here. The update request asks for the cart-items section HTML
 * (`sections`), and a `CartLinesUpdateEvent` carrying it is dispatched from this element;
 * `cart-items-component` (assets/component-cart-items.js, `#handleCartUpdate`) already morphs the
 * cart from exactly that payload for any update it didn't start itself, and the header cart count
 * listens for the same event. Reusing that path keeps one morph implementation instead of two.
 *
 * ⚠️ This owns its `<dialog>` directly instead of nesting a `dialog-component`: declarative
 * `on:click` handlers resolve to the *closest* component ancestor (assets/component.js), so pills
 * inside a nested `dialog-component` would dispatch to it rather than here. Escape and focus
 * trapping come from the native modal dialog; scroll lock and click-outside are added below.
 *
 * ⚠️ `close` / `cancel` don't bubble, and the dialog element can be swapped by a cart morph, so
 * those listeners are attached to *this* element in the capture phase rather than to the dialog.
 *
 * @typedef {{id: number, available: boolean, option1: string | null, option2: string | null, option3: string | null}} Variant
 * @typedef {{price: string, compare: string | null}} VariantPrice
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} price
 * @property {HTMLElement} comparePrice
 * @property {HTMLElement} message
 * @property {HTMLButtonElement} confirmButton
 * @extends Component<Refs>
 */
export class CartLineVariantPicker extends Component {
  requiredRefs = ['dialog', 'price', 'comparePrice', 'message', 'confirmButton'];

  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onBackdropClick);
    this.addEventListener('close', this.#onDialogClose, true);
    this.addEventListener('cancel', this.#onDialogCancel, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onBackdropClick);
    this.removeEventListener('close', this.#onDialogClose, true);
    this.removeEventListener('cancel', this.#onDialogCancel, true);
    unlockScroll(this);
  }

  showDialog() {
    this.#resetSelection();
    this.refs.dialog.showModal();
    lockScroll(this);
  }

  closeDialog() {
    if (this.#busy) return;
    this.refs.dialog.close();
  }

  /**
   * @param {Event} event - Proxied by assets/component.js so `target` is the clicked value button.
   */
  selectOption(event) {
    if (this.#busy) return;
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const group = button.closest('.cart-variant-dialog__values');
    if (!group) return;

    for (const value of group.querySelectorAll('.cart-variant-dialog__value')) {
      value.setAttribute('aria-checked', String(value === button));
    }

    this.#refresh();
  }

  async confirmChange() {
    if (this.#busy) return;

    const variant = this.#findVariant(this.#selectedValues());
    if (!variant || !variant.available || String(variant.id) === this.dataset.currentVariantId) return;

    const lineKey = this.dataset.lineKey;
    if (!lineKey) return;

    // Every line of this product when Shopify split it over several (shown as one row).
    const lineKeys = (this.dataset.lineKeys || lineKey).split(',').filter(Boolean);

    this.#setBusy(true);

    try {
      const added = await this.#addVariant(variant.id, Number(this.dataset.quantity) || 1);
      if (!added.ok) {
        this.#showMessage(added.message || Theme.translations.variant_change_failed);
        return;
      }

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
        .filter(Boolean);

      const response = await fetch(
        `${Theme.routes.cart_update_url}.js`,
        fetchConfig('json', {
          body: JSON.stringify({
            updates: Object.fromEntries(lineKeys.map((key) => [key, 0])),
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      /** @type {any} */
      const cart = await response.json();

      // The new variant is already in the cart at this point, so the cart must be refreshed even
      // if removing the old line failed — otherwise the page would show neither change. Omitting
      // `sections` makes cart-items-component fall back to a fresh section render.
      const failed = !response.ok || Boolean(cart?.status) || Boolean(cart?.errors);

      this.#setBusy(false);
      this.refs.dialog.close();

      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: [{ id: lineKey, quantity: 0 }],
          promise: deferred.promise,
        })
      );

      if (failed) {
        deferred.resolve({
          cart: null,
          detail: { source: 'cart-line-variant-picker', didError: true },
        });
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-line-variant-picker',
          didError: false,
        },
      });
    } catch (error) {
      console.error('[cart-line-variant-picker] Variant change failed:', error);
      this.#showMessage(Theme.translations.variant_change_failed);
    } finally {
      if (this.#busy) this.#setBusy(false);
    }
  }

  /**
   * @param {number} variantId
   * @param {number} quantity
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  async #addVariant(variantId, quantity) {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
    });
    /** @type {any} */
    const result = await response.json();

    if (!response.ok || result?.status) {
      return { ok: false, message: result?.description || result?.message };
    }

    return { ok: true };
  }

  /**
   * Clicks on the dialog element itself only happen on its backdrop: the panel inside fills the
   * dialog edge to edge, so any click on real content targets a descendant instead.
   * @param {MouseEvent} event
   */
  #onBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.closeDialog();
  };

  /** Fires for every way the dialog closes — the close button, Cancel, backdrop, or Escape. */
  #onDialogClose = () => {
    unlockScroll(this);
  };

  /**
   * Escape while a change is in flight would hide the dialog but leave the requests running.
   * @param {Event} event
   */
  #onDialogCancel = (event) => {
    if (this.#busy) event.preventDefault();
  };

  /** Back to this line's actual current variant — reopening never shows a half-made choice. */
  #resetSelection() {
    for (const value of this.querySelectorAll('.cart-variant-dialog__value')) {
      value.setAttribute('aria-checked', String(value.hasAttribute('data-current')));
    }
    this.#refresh();
  }

  /**
   * Re-derives everything the dialog shows from the current selection: which values are sold out
   * alongside the other chosen options, the "Size: M" labels, the price, the message, and whether
   * Confirm can be pressed.
   */
  #refresh() {
    const variants = this.#readJson('data-variants', /** @type {Variant[]} */ ([]));
    const selected = this.#selectedValues();

    this.querySelectorAll('.cart-variant-dialog__option').forEach((group, index) => {
      for (const value of group.querySelectorAll('.cart-variant-dialog__value')) {
        if (!(value instanceof HTMLElement)) continue;
        const candidate = [...selected];
        candidate[index] = value.dataset.value;
        const variant = this.#findVariant(candidate, variants);
        value.toggleAttribute('data-unavailable', !variant?.available);
      }

      const label = group.querySelector('.cart-variant-dialog__option-selected');
      if (label && selected[index] !== undefined) label.textContent = selected[index] ?? '';
    });

    const variant = this.#findVariant(selected, variants);
    const isCurrent = variant ? String(variant.id) === this.dataset.currentVariantId : false;

    if (variant) this.#showPrice(variant.id);

    if (!variant) {
      this.#showMessage(Theme.translations.variant_unavailable);
    } else if (!variant.available) {
      this.#showMessage(Theme.translations.variant_sold_out);
    } else {
      this.refs.message.hidden = true;
    }

    this.refs.confirmButton.disabled = !variant || !variant.available || isCurrent;
  }

  /** @param {number} variantId */
  #showPrice(variantId) {
    const prices = this.#readJson('data-prices', /** @type {Record<string, VariantPrice>} */ ({}));
    const entry = prices[String(variantId)];
    if (!entry) return;

    this.refs.price.textContent = entry.price;
    this.refs.comparePrice.textContent = entry.compare ?? '';
    this.refs.comparePrice.hidden = !entry.compare;
  }

  /** @returns {(string | undefined)[]} One value per option, in option order. */
  #selectedValues() {
    return Array.from(this.querySelectorAll('.cart-variant-dialog__option')).map((group) => {
      const checked = group.querySelector('.cart-variant-dialog__value[aria-checked="true"]');
      return checked instanceof HTMLElement ? checked.dataset.value : undefined;
    });
  }

  /**
   * @param {(string | undefined)[]} values
   * @param {Variant[]} [variants]
   * @returns {Variant | undefined}
   */
  #findVariant(values, variants = this.#readJson('data-variants', /** @type {Variant[]} */ ([]))) {
    if (values.some((value) => value === undefined)) return undefined;

    return variants.find((variant) =>
      values.every((value, index) => {
        const key = /** @type {'option1' | 'option2' | 'option3'} */ (`option${index + 1}`);
        return variant[key] === value;
      })
    );
  }

  /**
   * Parsed on every call rather than cached: a cart morph can reuse this element for a different
   * line's markup, and a cached list would then describe the wrong product.
   * @template T
   * @param {string} attribute
   * @param {T} fallback
   * @returns {T}
   */
  #readJson(attribute, fallback) {
    const script = this.querySelector(`script[${attribute}]`);
    if (!script?.textContent) return fallback;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn(`[cart-line-variant-picker] Could not parse ${attribute}:`, error);
      return fallback;
    }
  }

  /** @param {string} message */
  #showMessage(message) {
    this.refs.message.textContent = message;
    this.refs.message.hidden = false;
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('data-busy', busy);
    this.refs.confirmButton.disabled = busy;
    this.refs.confirmButton.setAttribute('aria-busy', String(busy));
  }
}

if (!customElements.get('cart-line-variant-picker')) {
  customElements.define('cart-line-variant-picker', CartLineVariantPicker);
}
